import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  archiveMedicationRecord,
  deleteUnfinalizedMedicationRecord,
} from '../../components/Dashboard/shared/medicationPersistence';
import { completeCareSourceMedicationCandidateDecision } from '../reviewableItems/medicationCandidateCompletion';
import { fetchDecisionReceiptsForCandidateKeys } from '../reviewableItems/reviewableDecisionReceipts';
import { buildCareSourceMedicationReviewCandidate } from '../../utils/medicationReviewCandidate';
import { markMedicationSourceReviewComplete } from './careSourceActivityService';
import {
  buildMedicationFromSuggestion,
  getMedicationSourceSuggestions,
} from './medicationSourceSuggestions';
import { buildMedicationPlanCandidates } from './medicationPlanCandidates';
import MedicationPlanCandidateReviewRow from './MedicationPlanCandidateReviewRow';
import MedicationSourceReviewFallback from './MedicationSourceReviewFallback';

const getSourceTitle = (source = {}) => (
  source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || 'Medication source'
);

const getMedicationSummary = (medication = {}) => {
  const schedules = Array.isArray(medication?.schedules) ? medication.schedules : [];
  const firstSchedule = schedules[0] || {};
  const dose = [firstSchedule.dose || medication?.dose || '', firstSchedule.unit || medication?.unit || '']
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
  const schedule = String(firstSchedule.time || medication?.schedule || medication?.sourceScheduleText || '').trim();
  return [medication?.name || '', dose, schedule].filter(Boolean).join(' · ');
};

const isCandidateReviewed = ({
  candidate,
  resolvedCandidateIds = [],
  ignoredCandidateIds = [],
} = {}) => (
  candidate?.suggestedAction === 'no_change'
  || resolvedCandidateIds.includes(candidate.id)
  || ignoredCandidateIds.includes(candidate.id)
);

const getCandidateDecisionLabel = ({
  candidate,
  resolvedCandidateIds = [],
  ignoredCandidateIds = [],
  editingCandidateIds = [],
} = {}) => {
  if (candidate?.suggestedAction === 'no_change') return 'No change needed';
  if (resolvedCandidateIds.includes(candidate.id)) {
    return candidate.suggestedAction === 'temporary_hold' ? 'Temporary hold applied' : 'Added to medication list';
  }
  if (ignoredCandidateIds.includes(candidate.id)) return 'Ignored';
  if (editingCandidateIds.includes(candidate.id)) return 'Editing now';
  return 'Needs decision';
};

const MedicationSourceReview = ({
  source = {},
  existingMedications = [],
  open = false,
  onError,
  onAddMedication,
  onMedicationListChanged,
  onReviewComplete,
  medicationReviewNavigation = null,
  createTimelineEvent = false,
  reviewMissingActiveMedications = true,
  pageLayout = false,
  onBack,
  onSetReminder,
  reminderStateByMedicationId,
  childName = '',
}) => {
  const [savingCandidateId, setSavingCandidateId] = useState('');
  const [pausingMedicationId, setPausingMedicationId] = useState('');
  const [resolvedCandidateIds, setResolvedCandidateIds] = useState([]);
  const [ignoredCandidateIds, setIgnoredCandidateIds] = useState([]);
  const [decidedMedicationByCandidateId, setDecidedMedicationByCandidateId] = useState({});
  const [editingCandidateIds, setEditingCandidateIds] = useState([]);
  const [keptMedicationIds, setKeptMedicationIds] = useState([]);
  const [pausedMedicationIds, setPausedMedicationIds] = useState([]);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [completingReview, setCompletingReview] = useState(false);
  const suggestions = useMemo(() => getMedicationSourceSuggestions(source), [source]);
  const hasSuggestions = suggestions.length > 0;
  const activeMedications = useMemo(
    () => existingMedications.filter((medication) => !medication?.isArchived),
    [existingMedications]
  );
  const candidates = useMemo(() => buildMedicationPlanCandidates({
    suggestions,
    source,
    existingMedications: activeMedications,
  }), [activeMedications, source, suggestions]);
  const matchedMedicationIds = useMemo(() => new Set(
    candidates
      .map((candidate) => candidate.matchedMedication?.id)
      .filter(Boolean)
  ), [candidates]);
  const missingActiveMedications = reviewMissingActiveMedications ? activeMedications.filter((medication) => {
    const id = String(medication?.id || '').trim();
    return id && !matchedMedicationIds.has(id);
  }) : [];
  const hasResolvedSuggestions = candidates.every((candidate) => (
    isCandidateReviewed({
      candidate,
      resolvedCandidateIds,
      ignoredCandidateIds,
    })
  ));
  const hasResolvedMissingMedications = missingActiveMedications.every((medication) => (
    keptMedicationIds.includes(medication.id)
    || pausedMedicationIds.includes(medication.id)
  ));
  const canCompleteReview = hasSuggestions
    ? hasResolvedSuggestions && hasResolvedMissingMedications
    : true;
  const missingDecisionCount = missingActiveMedications.filter((medication) => (
    keptMedicationIds.includes(medication.id)
    || pausedMedicationIds.includes(medication.id)
  )).length;
  const reviewedCandidateCount = candidates.filter((candidate) => (
    isCandidateReviewed({
      candidate,
      resolvedCandidateIds,
      ignoredCandidateIds,
    })
  )).length;
  const totalDecisionCount = candidates.length + missingActiveMedications.length;
  const decidedCount = reviewedCandidateCount + missingDecisionCount;
  const matchedCandidates = candidates.filter((candidate) => (
    candidate.suggestedAction === 'no_change' || Boolean(candidate.matchedMedication)
  ));
  const newCandidates = candidates.filter((candidate) => (
    candidate.suggestedAction !== 'no_change' && !candidate.matchedMedication
  ));
  const sourceTitle = getSourceTitle(source);
  const isTimelineSource = String(source.sourceContext || source.sourceMode || '').trim().toLowerCase() === 'timeline';
  const hasMedicationReviewNavigation = Boolean(
    medicationReviewNavigation?.returnTo
    && (medicationReviewNavigation?.originEntryId || medicationReviewNavigation?.originDate || medicationReviewNavigation?.originSourceId)
  );

  useEffect(() => {
    setReviewComplete(false);
    setCompletingReview(false);
    setResolvedCandidateIds([]);
    setIgnoredCandidateIds([]);
    setDecidedMedicationByCandidateId({});
    setEditingCandidateIds([]);
    setKeptMedicationIds([]);
    setPausedMedicationIds([]);
  }, [source.id]);

  useEffect(() => {
    let active = true;
    const candidatesByKey = new Map(candidates.map((candidate) => [
      buildCareSourceMedicationReviewCandidate({ candidate, source }).key,
      candidate,
    ]));
    const keys = [...candidatesByKey.keys()].filter(Boolean);
    if (!keys.length) return () => { active = false; };

    Promise.resolve(fetchDecisionReceiptsForCandidateKeys(keys)).then((receipts) => {
      if (!active) return;
      const resolved = [];
      const ignored = [];
      const decidedMedications = {};
      (receipts || []).forEach((receipt) => {
        const candidate = candidatesByKey.get(receipt.candidateKey || receipt.id);
        if (!candidate?.id) return;
        if (receipt.action === 'ignored') ignored.push(candidate.id);
        else if (receipt.action === 'resolved' || receipt.action === 'auto_applied') {
          resolved.push(candidate.id);
          const targetMedication = activeMedications.find((medication) => medication.id === receipt.target?.id);
          if (targetMedication) {
            decidedMedications[candidate.id] = {
              decisionAction: receipt.decisionAction || candidate.suggestedAction,
              medication: targetMedication,
            };
          }
        }
      });
      setResolvedCandidateIds((current) => [...new Set([...current, ...resolved])]);
      setIgnoredCandidateIds((current) => [...new Set([...current, ...ignored])]);
      setDecidedMedicationByCandidateId((current) => ({
        ...decidedMedications,
        ...current,
      }));
    }).catch(() => {});

    return () => { active = false; };
  }, [activeMedications, candidates, source]);

  const handleMedicationListChanged = (change) => {
    onMedicationListChanged?.(change);
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('captureez:medications-refresh', {
        detail: {
          childId: source.childId || null,
          sourceId: source.id || null,
          change,
        },
      }));
    }
  };
  const buildReminderPath = (medication = {}) => {
    if (!source.childId || !source.id || !medication.id) return '';
    const reviewPath = `/children/${source.childId}/medications/review/${source.id}`;
    const params = new URLSearchParams({
      medicationId: medication.id,
      returnTo: reviewPath,
    });
    return `/children/${source.childId}/medication-reminders?${params.toString()}`;
  };

  const handleMarkReviewComplete = async () => {
    if (!source.id || completingReview || reviewComplete || !canCompleteReview) return;

    setCompletingReview(true);
    onError?.('');
    try {
      await markMedicationSourceReviewComplete(source.id, {
        childId: source.childId || '',
        sourceTitle,
        foundCount: candidates.length,
        addedCount: candidates.filter((candidate) => (
          resolvedCandidateIds.includes(candidate.id) && candidate.suggestedAction === 'add_new'
        )).length,
        updatedCount: candidates.filter((candidate) => (
          resolvedCandidateIds.includes(candidate.id) && candidate.suggestedAction === 'update_existing'
        )).length,
        unchangedCount: candidates.filter((candidate) => candidate.suggestedAction === 'no_change').length,
        notAddedCount: ignoredCandidateIds.length,
      });
      setReviewComplete(true);
      if (hasMedicationReviewNavigation) {
        window.setTimeout(() => {
          onReviewComplete?.({
            action: 'mark_reviewed',
            navigation: medicationReviewNavigation,
            sourceId: source.id || null,
          });
        }, 0);
      }
    } catch (error) {
      onError?.(error?.message || "We couldn't mark this medication source reviewed right now.");
    } finally {
      setCompletingReview(false);
    }
  };

  const handleSaveCandidate = async (candidate) => {
    if (!candidate?.id || savingCandidateId || candidate.suggestedAction === 'no_change') return;

    setSavingCandidateId(candidate.id);
    onError?.('');
    try {
      const priorDecision = decidedMedicationByCandidateId[candidate.id];
      const isCorrection = priorDecision?.decisionAction === 'add_new' && priorDecision.medication?.id;
      const isUpdate = candidate.suggestedAction === 'update_existing'
        || candidate.suggestedAction === 'temporary_hold'
        || isCorrection;
      const medicationDraft = candidate.nextMedication || buildMedicationFromSuggestion(candidate.suggestion, source);
      const decisionAction = candidate.suggestedAction === 'temporary_hold'
        ? 'temporary_hold'
        : candidate.suggestedAction === 'complete_course'
        ? 'complete_course'
        : isUpdate ? 'update_existing' : 'add_new';
      const savedMedication = await completeCareSourceMedicationCandidateDecision({
        candidate,
        source,
        medication: isCorrection ? { ...medicationDraft, id: priorDecision.medication.id } : medicationDraft,
        previousMedication: isCorrection ? priorDecision.medication : (isUpdate || decisionAction === 'complete_course') ? candidate.matchedMedication : null,
        decisionAction,
        createTimelineEvent,
      });
      setResolvedCandidateIds((current) => (
        current.includes(candidate.id) ? current : [...current, candidate.id]
      ));
      setDecidedMedicationByCandidateId((current) => ({
        ...current,
        [candidate.id]: {
          decisionAction: isCorrection ? 'add_new' : candidate.suggestedAction,
          medication: savedMedication,
        },
      }));
      handleMedicationListChanged({
        type: candidate.suggestedAction === 'temporary_hold' ? 'temporary_hold' : candidate.suggestedAction === 'complete_course' ? 'course_completed' : isUpdate ? 'updated' : 'added',
        medication: savedMedication,
      });
      if (hasMedicationReviewNavigation) {
        window.setTimeout(() => {
          onReviewComplete?.({
            action: isUpdate ? 'update' : 'add',
            navigation: medicationReviewNavigation,
            sourceId: source.id || null,
            candidateId: candidate.id,
          });
        }, 0);
      }
    } catch (error) {
      onError?.(error?.message || "We couldn't save this medication plan right now.");
    } finally {
      setSavingCandidateId('');
    }
  };

  const handleEditFirst = (candidate) => {
    if (!candidate?.id) return;

    setEditingCandidateIds((current) => (
      current.includes(candidate.id) ? current : [...current, candidate.id]
    ));
    setResolvedCandidateIds((current) => current.filter((candidateId) => candidateId !== candidate.id));
    setIgnoredCandidateIds((current) => current.filter((candidateId) => candidateId !== candidate.id));
  };

  const handleIgnoreCandidate = async (candidate) => {
    if (!candidate?.id || savingCandidateId) return;
    setSavingCandidateId(candidate.id);
    onError?.('');
    try {
      const priorDecision = decidedMedicationByCandidateId[candidate.id];
      if (priorDecision?.decisionAction === 'add_new' && priorDecision.medication?.id) {
        await deleteUnfinalizedMedicationRecord({
          childId: source.childId || '',
          medicationId: priorDecision.medication.id,
        });
        handleMedicationListChanged({ type: 'removed', medication: priorDecision.medication });
      }
      await completeCareSourceMedicationCandidateDecision({
        candidate,
        source,
        decisionAction: 'ignore',
      });
      setIgnoredCandidateIds((current) => (
        current.includes(candidate.id) ? current : [...current, candidate.id]
      ));
      setResolvedCandidateIds((current) => current.filter((candidateId) => candidateId !== candidate.id));
      setDecidedMedicationByCandidateId((current) => {
        const next = { ...current };
        delete next[candidate.id];
        return next;
      });
      if (hasMedicationReviewNavigation) {
        window.setTimeout(() => {
          onReviewComplete?.({
            action: 'skip',
            navigation: medicationReviewNavigation,
            sourceId: source.id || null,
            candidateId: candidate.id,
          });
        }, 0);
      }
    } catch (error) {
      onError?.(error?.message || "We couldn't save that medication review decision right now.");
    } finally {
      setSavingCandidateId('');
    }
  };

  const handleKeepActive = (medication) => {
    if (!medication?.id) return;

    setKeptMedicationIds((current) => (
      current.includes(medication.id) ? current : [...current, medication.id]
    ));
  };

  const handlePauseMedication = async (medication) => {
    if (!medication?.id || pausingMedicationId) return;

    setPausingMedicationId(medication.id);
    onError?.('');
    try {
      const pausedMedication = await archiveMedicationRecord({
        childId: source.childId || '',
        medication,
        archived: true,
        createTimelineEvent: false,
      });
      setPausedMedicationIds((current) => (
        current.includes(medication.id) ? current : [...current, medication.id]
      ));
      handleMedicationListChanged({ type: 'paused', medication: pausedMedication });
    } catch (error) {
      onError?.(error?.message || "We couldn't pause this medication right now.");
    } finally {
      setPausingMedicationId('');
    }
  };

  return (
    <Collapse in={open} unmountOnExit sx={{ width: '100%' }}>
      <Box
        sx={{
          mt: pageLayout ? 0 : 1,
          minHeight: pageLayout ? '100dvh' : 'auto',
          borderRadius: pageLayout ? 0 : 2.5,
          border: pageLayout ? 'none' : `1px solid ${alpha(colors.brand.deep, 0.28)}`,
          bgcolor: colors.semantic.profileFlow.cardBg,
          overflow: 'hidden',
        }}
      >
        {pageLayout ? (
          <Box sx={{ position: 'sticky', top: 0, zIndex: 3, px: { xs: 2, sm: 3 }, pt: 1.8, pb: 1.2, bgcolor: colors.semantic.profileFlow.cardBg, borderBottom: `1px solid ${colors.semantic.profileFlow.cardBorder}` }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Button onClick={onBack} sx={{ minWidth: 0, p: 0, textTransform: 'none', color: colors.semantic.profileFlow.medicationManager.actionText, fontWeight: 850 }}>
                Back
              </Button>
              <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.8rem', fontWeight: 800 }}>
                {decidedCount} of {totalDecisionCount} decided
              </Typography>
            </Stack>
            <Typography component="h1" sx={{ mt: 1.25, color: colors.landing.heroText, fontSize: { xs: '1.45rem', sm: '1.7rem' }, fontWeight: 900, lineHeight: 1.1 }}>
              Review uploaded source
            </Typography>
            <Typography sx={{ mt: 0.45, color: colors.semantic.ink2, fontSize: '0.86rem' }}>{sourceTitle}</Typography>
            <Box sx={{ mt: 1.1, height: 5, borderRadius: '999px', bgcolor: colors.semantic.profileFlow.notStartedBg, overflow: 'hidden' }}>
              <Box sx={{ width: `${totalDecisionCount ? (decidedCount / totalDecisionCount) * 100 : 0}%`, height: '100%', bgcolor: colors.semantic.profileFlow.medicationManager.primaryAction }} />
            </Box>
          </Box>
        ) : null}
        {!pageLayout ? (
        <Box
          sx={{
            p: { xs: 1.15, sm: 1.35 },
            bgcolor: alpha(colors.brand.deep, 0.06),
            borderBottom: `1px solid ${alpha(colors.brand.deep, 0.16)}`,
          }}
        >
          <Stack spacing={0.85}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, mt: 0.1, color: colors.brand.deep, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: colors.brand.deep,
                    lineHeight: 1.2,
                  }}
                >
                  Uploaded source · 1 file
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: '0.96rem',
                    fontWeight: 900,
                    color: colors.landing.heroText,
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                  }}
                >
                  {sourceTitle}
                </Typography>
              </Box>
            </Stack>
            {hasSuggestions ? (
              <Stack direction="row" spacing={0.85} alignItems="center">
                <Box sx={{ flex: 1, height: 8, borderRadius: '999px', bgcolor: alpha(colors.brand.deep, 0.14), overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${totalDecisionCount ? Math.round((decidedCount / totalDecisionCount) * 100) : 0}%`,
                      height: '100%',
                      borderRadius: '999px',
                      bgcolor: colors.brand.deep,
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: colors.brand.deep, fontWeight: 900, whiteSpace: 'nowrap' }}>
                  {decidedCount} of {totalDecisionCount} decided
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>
        ) : null}

        <Stack spacing={1.15} sx={{ p: pageLayout ? { xs: 2, sm: 3 } : { xs: 1.15, sm: 1.35 } }}>
          <Box sx={{ p: 1.1, borderRadius: 2, bgcolor: colors.semantic.profileFlow.notStartedBg }}>
            <Typography sx={{ color: colors.semantic.ink, fontSize: '0.86rem', lineHeight: 1.45 }}>
              {pageLayout
                ? <>Nothing reaches the medication list until you decide. {activeMedications.length ? `${activeMedications.length} active medication${activeMedications.length === 1 ? '' : 's'} ${activeMedications.length === 1 ? 'is' : 'are'} on the list now.` : `${childName || 'This person'} has no active medications on the list yet, so ${candidates.length === 1 ? 'this one is' : `all ${candidates.length} are`} new.`}</>
                : <><Box component="span" sx={{ fontWeight: 900 }}>Nothing is added to the master medication list without your review.</Box> Compare the uploaded source against the current list before you add or update anything.</>}
            </Typography>
          </Box>

          {!pageLayout ? (
          <Box sx={{ p: 1.1, borderRadius: 2, bgcolor: colors.semantic.profileFlow.cardBg, border: `1px solid ${alpha(colors.brand.deep, 0.14)}` }}>
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: colors.semantic.ink }}>
                Current medications
              </Typography>
              {activeMedications.length ? (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {activeMedications.map((medication) => (
                    <Box
                      key={medication.id}
                      component="span"
                      sx={{
                        px: 0.8,
                        py: 0.35,
                        borderRadius: '999px',
                        bgcolor: colors.semantic.profileFlow.notStartedBg,
                        color: colors.semantic.ink,
                        fontSize: '0.74rem',
                        fontWeight: 800,
                        lineHeight: 1.25,
                      }}
                    >
                      {getMedicationSummary(medication) || medication.name || 'Medication'}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.4 }}>
                  No active medications are on the master list yet.
                </Typography>
              )}
            </Stack>
          </Box>
          ) : null}

          {hasSuggestions ? (
            <Box>
              {!pageLayout ? <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.6, flexWrap: 'wrap', rowGap: 0.4 }}>
                <Box component="span" sx={{ px: 0.8, py: 0.25, borderRadius: '999px', bgcolor: colors.semantic.profileFlow.completionBg, color: colors.semantic.profileFlow.completionText, fontSize: '0.78rem', fontWeight: 900 }}>
                  {isTimelineSource ? 'Matched on the master list' : 'Already on your list'}
                </Box>
                <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.8rem', fontWeight: 850 }}>
                  {matchedCandidates.length} matched
                </Typography>
              </Stack> : null}
              {!pageLayout ? <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.82rem', lineHeight: 1.4, mb: matchedCandidates.length ? 0.85 : 0 }}>
                {isTimelineSource
                  ? 'This note mentions medication already in the master list. Review the detected dose and timing before changing anything.'
                  : 'Found in the document and already in the profile. Confirm the details still look right.'}
              </Typography> : null}
              {matchedCandidates.length ? (
                <Stack spacing={0.85}>
                  {matchedCandidates.map((candidate) => {
                    const suggestion = candidate.suggestion;
                    const isResolved = resolvedCandidateIds.includes(candidate.id);
                    const isIgnored = ignoredCandidateIds.includes(candidate.id);
                    const isEditing = editingCandidateIds.includes(candidate.id);
                    const isSaving = savingCandidateId === candidate.id;
                    const decisionLabel = getCandidateDecisionLabel({ candidate, resolvedCandidateIds, ignoredCandidateIds, editingCandidateIds });

                    return (
                      <MedicationPlanCandidateReviewRow
                        key={suggestion.id}
                        candidate={candidate}
                        sourceTitle={sourceTitle}
                        isResolved={isResolved}
                        isIgnored={isIgnored}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        decisionLabel={decisionLabel}
                        onSave={handleSaveCandidate}
                        onEditFirst={handleEditFirst}
                        onIgnore={handleIgnoreCandidate}
                        onSetReminder={onSetReminder}
                        reminderState={reminderStateByMedicationId?.get(decidedMedicationByCandidateId[candidate.id]?.medication?.id)}
                        resolvedMedication={decidedMedicationByCandidateId[candidate.id]?.medication}
                        reminderPath={buildReminderPath(decidedMedicationByCandidateId[candidate.id]?.medication)}
                        pageLayout={pageLayout}
                      />
                    );
                  })}
                </Stack>
              ) : null}
            </Box>
          ) : null}

          {newCandidates.length ? (
            <Box>
              {!pageLayout ? <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.6, flexWrap: 'wrap', rowGap: 0.4 }}>
                <Box component="span" sx={{ px: 0.8, py: 0.25, borderRadius: '999px', bgcolor: alpha(colors.brand.deep, 0.1), color: colors.brand.deep, fontSize: '0.78rem', fontWeight: 900 }}>
                  {isTimelineSource ? 'Not in the master list' : 'New from document'}
                </Box>
                <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.8rem', fontWeight: 850 }}>
                  {newCandidates.length} to decide
                </Typography>
              </Stack> : null}
              {!pageLayout ? <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.82rem', lineHeight: 1.4, mb: 0.85 }}>
                {isTimelineSource
                  ? 'This note mentions a new medication. Review details before adding it to the master list.'
                  : 'These are not in the current profile. Review details before adding them to the list.'}
              </Typography> : null}
              <Stack spacing={0.85}>
                {newCandidates.map((candidate) => {
                  const suggestion = candidate.suggestion;
                  const isResolved = resolvedCandidateIds.includes(candidate.id);
                  const isIgnored = ignoredCandidateIds.includes(candidate.id);
                  const isEditing = editingCandidateIds.includes(candidate.id);
                  const isSaving = savingCandidateId === candidate.id;
                  const decisionLabel = getCandidateDecisionLabel({ candidate, resolvedCandidateIds, ignoredCandidateIds, editingCandidateIds });

                  return (
                    <MedicationPlanCandidateReviewRow
                      key={suggestion.id}
                      candidate={candidate}
                      sourceTitle={sourceTitle}
                      isResolved={isResolved}
                      isIgnored={isIgnored}
                      isEditing={isEditing}
                      isSaving={isSaving}
                      decisionLabel={decisionLabel}
                      onSave={handleSaveCandidate}
                      onEditFirst={handleEditFirst}
                      onIgnore={handleIgnoreCandidate}
                      onSetReminder={onSetReminder}
                      reminderState={reminderStateByMedicationId?.get(decidedMedicationByCandidateId[candidate.id]?.medication?.id)}
                      resolvedMedication={decidedMedicationByCandidateId[candidate.id]?.medication}
                      reminderPath={buildReminderPath(decidedMedicationByCandidateId[candidate.id]?.medication)}
                      pageLayout={pageLayout}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          {!hasSuggestions ? (
            <Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 850, color: colors.semantic.ink }}>
                Review before adding
              </Typography>
              <Typography sx={{ mt: 0.25, mb: 0.85, fontSize: '0.76rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.4 }}>
                No medication details were found automatically. Review the source and add medications manually if needed.
              </Typography>
              <MedicationSourceReviewFallback onAddMedication={onAddMedication} />
            </Box>
          ) : null}
          {hasSuggestions && missingActiveMedications.length ? (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.6, flexWrap: 'wrap', rowGap: 0.4 }}>
                <Box component="span" sx={{ px: 0.8, py: 0.25, borderRadius: '999px', bgcolor: alpha(colors.semantic.warning, 0.14), color: colors.semantic.profileFlow.inProgressText, fontSize: '0.78rem', fontWeight: 900 }}>
                  Not in this document
                </Box>
                <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.8rem', fontWeight: 850 }}>
                  {missingActiveMedications.length - missingDecisionCount} to decide
                </Typography>
              </Stack>
              <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.82rem', lineHeight: 1.4, mb: 0.85 }}>
                Active in the profile but not mentioned in this file. That does not always mean they were stopped — choose what to do with each.
              </Typography>
              <Stack spacing={0.85}>
              {missingActiveMedications.map((medication) => {
                const isKept = keptMedicationIds.includes(medication.id);
                const isPaused = pausedMedicationIds.includes(medication.id);
                const isBusy = pausingMedicationId === medication.id;
                const name = medication.name || 'Medication';

                return (
                  <Stack
                    key={medication.id}
                    spacing={0.85}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                      bgcolor: colors.semantic.profileFlow.cardBg,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.2 }}>
                        {name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: colors.semantic.ink2, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        Active in profile
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.6} alignItems="stretch">
                      <Button
                        variant="outlined"
                        disabled={isKept || isPaused || isBusy}
                        onClick={() => handleKeepActive(medication)}
                        sx={{
                          flex: 1,
                          minHeight: 38,
                          borderRadius: 1.75,
                          px: 1,
                          fontSize: '0.82rem',
                          fontWeight: 900,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          borderColor: isKept ? colors.semantic.sage : colors.semantic.profileFlow.cardBorder,
                          bgcolor: isKept ? colors.semantic.profileFlow.completionBg : colors.semantic.profileFlow.cardBg,
                          color: isKept ? colors.semantic.profileFlow.completionText : colors.semantic.ink2,
                        }}
                      >
                        {isKept ? 'Kept active' : 'Keep active'}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={isKept || isPaused || isBusy}
                        onClick={() => handlePauseMedication(medication)}
                        sx={{
                          flex: 1,
                          minHeight: 38,
                          borderRadius: 1.75,
                          px: 1,
                          fontSize: '0.82rem',
                          fontWeight: 900,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          color: isPaused ? colors.semantic.ink : colors.semantic.ink2,
                          borderColor: isPaused ? colors.semantic.profileFlow.cardBorder : alpha(colors.semantic.ink, 0.18),
                          bgcolor: isPaused ? colors.semantic.profileFlow.notStartedBg : colors.semantic.profileFlow.cardBg,
                        }}
                      >
                        {isPaused ? 'Paused' : isBusy ? 'Pausing...' : 'Mark as paused'}
                      </Button>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
          ) : null}
        {pageLayout ? (
          <Box
            data-testid="medication-review-scroll-clearance"
            aria-hidden="true"
            sx={{ minHeight: 'calc(112px + env(safe-area-inset-bottom))' }}
          />
        ) : null}
        <Stack
          direction={pageLayout ? 'row' : { xs: 'column', sm: 'row' }}
          spacing={0.75}
          alignItems={pageLayout ? 'center' : { xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={pageLayout ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4, px: 2, py: 1.5, bgcolor: colors.semantic.profileFlow.cardBg, borderTop: `1px solid ${colors.semantic.profileFlow.cardBorder}` } : { mt: 1.1, pt: 1, borderTop: `1px solid ${alpha(colors.brand.tint, 0.22)}` }}
        >
          <Typography sx={{ fontSize: '0.76rem', color: alpha(colors.semantic.ink, 0.68), lineHeight: 1.35 }}>
            {pageLayout && !reviewComplete
              ? `${Math.max(0, totalDecisionCount - decidedCount)} still to decide`
              : reviewComplete
              ? 'Medication source reviewed.'
              : hasSuggestions
                ? canCompleteReview
                  ? 'Every medication has a decision.'
                  : 'Decide on every medication to finish.'
                : 'Mark this source reviewed after checking whether the medication list needs changes.'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            disabled={!source.id || !canCompleteReview || completingReview || reviewComplete}
            onClick={handleMarkReviewComplete}
            sx={{
              borderRadius: '999px',
              px: 1.35,
              fontSize: '0.76rem',
              fontWeight: 850,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              boxShadow: 'none',
              bgcolor: colors.brand.deep,
              '&:hover': {
                bgcolor: colors.brand.ink,
                boxShadow: 'none',
              },
            }}
          >
            {reviewComplete ? 'Reviewed' : completingReview ? 'Finishing...' : pageLayout ? 'Done' : 'Mark reviewed'}
          </Button>
        </Stack>
        </Stack>
      </Box>
    </Collapse>
  );
};

export default MedicationSourceReview;
